(() => {
  const STORAGE_KEY = 'recently_viewed';
  const MAX_STORED = 12;
  const HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9\-]*[a-z0-9])?$/i;
  const memoryStore = { handles: [] };

  function isValidHandle(handle) {
    return typeof handle === 'string' && HANDLE_PATTERN.test(handle) && handle.length < 255;
  }

  function normalizeHandle(handle) {
    if (typeof handle !== 'string') return '';
    try {
      return decodeURIComponent(handle).trim().toLowerCase();
    } catch (error) {
      return handle.trim().toLowerCase();
    }
  }

  function sanitizeHandles(handles) {
    if (!Array.isArray(handles)) return [];
    const unique = [];
    handles.forEach((handle) => {
      const normalized = normalizeHandle(handle);
      if (isValidHandle(normalized) && unique.indexOf(normalized) === -1) {
        unique.push(normalized);
      }
    });
    return unique.slice(0, MAX_STORED);
  }

  function readHandles() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return memoryStore.handles.slice();
      return sanitizeHandles(JSON.parse(raw));
    } catch (error) {
      return memoryStore.handles.slice();
    }
  }

  function writeHandles(handles) {
    const next = sanitizeHandles(handles);
    memoryStore.handles = next.slice();
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      // Keep the in-memory copy when localStorage is blocked or full.
    }
    return next;
  }

  function recordHandle(handle) {
    const normalized = normalizeHandle(handle);
    if (!isValidHandle(normalized)) return readHandles();
    const next = readHandles().filter((item) => item !== normalized);
    next.unshift(normalized);
    return writeHandles(next);
  }

  function removeHandles(handlesToRemove) {
    if (!handlesToRemove || !handlesToRemove.length) return readHandles();
    const blocked = handlesToRemove.map(normalizeHandle);
    return writeHandles(readHandles().filter((handle) => blocked.indexOf(handle) === -1));
  }

  function getCurrentHandle(preferred) {
    const fromPreferred = normalizeHandle(preferred);
    if (isValidHandle(fromPreferred)) return fromPreferred;

    const analyticsHandle = normalizeHandle(
      (window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product &&
        window.ShopifyAnalytics.meta.product.handle) ||
        (window.meta && window.meta.product && window.meta.product.handle) ||
        ''
    );
    if (isValidHandle(analyticsHandle)) return analyticsHandle;

    const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? normalizeHandle(match[1]) : '';
  }

  function trackCurrentProduct() {
    if (!/\/products\//i.test(window.location.pathname)) return;
    const handle = getCurrentHandle();
    if (handle) recordHandle(handle);
  }

  function rootUrl() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  function buildSearchUrl(handles) {
    const query = handles.map((handle) => `handle:${handle}`).join(' OR ');
    const params = new URLSearchParams({
      q: query,
      type: 'product',
      view: 'recently-viewed',
      section_id: 'recently-viewed-product-cards'
    });
    return `${rootUrl()}search?${params.toString()}`;
  }

  trackCurrentProduct();

  if (customElements.get('recently-viewed-products')) return;

  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      connectedCallback() {
        this.abortController = null;
        this.requestId = 0;
        this.style.display = 'contents';

        if (this.hasFallbackItems()) {
          this.showSection();
        } else {
          this.hideSection();
        }

        this.loadProducts();
      }

      get sectionRoot() {
        return this.closest('.recently-viewed-products') || this;
      }

      hasFallbackItems() {
        return !!this.querySelector('slideshow-container-item');
      }

      showSection() {
        const root = this.sectionRoot;
        root.hidden = false;
        root.style.display = 'block';
      }

      hideSection() {
        const root = this.sectionRoot;
        root.hidden = true;
        root.style.display = 'none';
      }

      disconnectedCallback() {
        this.cancelRequest();
      }

      cancelRequest() {
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
      }

      loadProducts() {
        const currentHandle = getCurrentHandle(this.dataset.productHandle);
        if (currentHandle) recordHandle(currentHandle);

        const limit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 8, 1), MAX_STORED);
        const stored = readHandles();
        const displayHandles = stored.filter((handle) => handle !== currentHandle).slice(0, limit);

        if (!displayHandles.length) {
          if (this.hasFallbackItems()) {
            this.showSection();
          } else {
            this.hideSection();
          }
          return;
        }

        this.cancelRequest();
        this.abortController = new AbortController();
        const requestId = (this.requestId += 1);

        fetch(buildSearchUrl(displayHandles), {
          signal: this.abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then((response) => {
            if (!response.ok) throw new Error('Recently viewed request failed');
            return response.text();
          })
          .then((html) => {
            if (requestId !== this.requestId) return;
            this.renderResults(html, displayHandles);
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') return;
            if (this.hasFallbackItems()) {
              this.showSection();
            } else {
              this.hideSection();
            }
          });
      }

      renderResults(html, requestedHandles) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const found = {};

        doc.querySelectorAll('[data-product-handle]').forEach((item) => {
          const handle = normalizeHandle(item.getAttribute('data-product-handle'));
          if (isValidHandle(handle) && !found[handle]) {
            found[handle] = item;
          }
        });

        const missing = requestedHandles.filter((handle) => !found[handle]);
        if (missing.length) removeHandles(missing);

        const orderedItems = requestedHandles.map((handle) => found[handle]).filter(Boolean);
        if (!orderedItems.length) {
          if (this.hasFallbackItems()) {
            this.showSection();
          } else {
            this.hideSection();
          }
          return;
        }

        const wrapper = this.querySelector('[data-recently-viewed-slider]');
        const container = this.querySelector('slideshow-container');
        if (!wrapper || !container) return;

        if (this.dataset.showAddToCart === 'false') {
          orderedItems.forEach((item) => {
            item.querySelectorAll('atc-form').forEach((node) => node.remove());
            item.querySelectorAll('.product-form').forEach((node) => {
              if (!node.closest('[data-preview-size-grid], .preview-size-qv')) {
                node.remove();
              }
            });
          });
        }

        // Destroy existing swiper before swapping DOM nodes
        if (container.swiper && !container.swiper.destroyed) {
          container.swiper.destroy(true, true);
          container.swiper = null;
        }

        wrapper.replaceChildren(...orderedItems.map((item) => document.importNode(item, true)));
        this.showSection();

        container._initialized = false;
        delete container.dataset.initialized;
        container.init();

        if (typeof window.initProductPreviewSizes === 'function') {
          window.initProductPreviewSizes();
        }
      }
    }
  );
})();
